import { useEffect, useMemo, useRef, useState } from "react";
import { LiveMediaHelper, safeSetTimeout, ServerTimeHelper } from "@kaltura/mediaspace-shared-utils";
import { CommonHeroLayout } from "../layout/CommonHeroLayout";
import Lobby from "../lobby/Lobby";
import { HeroProps } from "../HeroProps";
import { LiveMediaPlayerV3, LiveMediaPlayerV3Props, Player } from "@kaltura/mediaspace-shared-player";
import { KalturaBaseEntry } from "kaltura-typescript-client/api/types/KalturaBaseEntry";
import { KalturaLiveStreamScheduleEvent } from "kaltura-typescript-client/api/types/KalturaLiveStreamScheduleEvent";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config";
import { getPropsToPass, useThumbnailSizing } from "../helpers";
import { MediaLobbyDataContainer } from "../lobby/lobby-data/LobbyData";
import { useCncReactions } from "@mediaspace/features/cnc";
import { KmsTypeMediaSchedulingData } from "@kaltura/mediaspace-shared-types";

export interface VirtualEventHeroEntry extends KalturaBaseEntry {
    recordedEntryId: string;
}

export interface VirtualEventHeroProps extends Omit<HeroProps, "entry" | "schedulingData">, LiveMediaPlayerV3Props {
    scheduleEvent?: KalturaLiveStreamScheduleEvent;
    schedulingData: KmsTypeMediaSchedulingData;
    entry: VirtualEventHeroEntry;
    playerConfig: any;
}

/**
 * wrapper component for virtual event media page's hero - lobby and player
 * handles Manual live and Simulive entries.
 */
const VirtualEventHero = (props: VirtualEventHeroProps) => {
    const {
        schedulingData,
        playlist,
        playerConfig,
        scheduleEvent,
        entry,
        actions = [],
        thumbnailUrl,
        isReadOnly,
    } = props;

    const config = useConfig();
    const serviceUrl = config.application.serviceUrl;

    const STREAM_POLL_INTERVAL = 10000;
    const ONE_HOUR = 3600000; /* 60*60*1000 */
    const TWELVE_HOURS = 43200000; /* 60*60*1000*12 */
    const [player, setPlayer] = useState<any>(null);
    const [showLobby, setShowLobby] = useState(true);
    const [vodPlayerConfig, setVodPlayerConfig] = useState({});
    const pollingInterval = useRef(0);

    const { startReactions, endReactions } = useCncReactions();

    const setupPlayerListeners = (playerInstance: any) => {
        playerInstance.addEventListener(playerInstance.Event.Core.PLAYER_DESTROY, teardownPlayerListeners);
        playerInstance.addEventListener(playerInstance.Event.Core.PLAYBACK_ENDED, handlePlaybackEnd);
        if (playlist) {
            playerInstance.plugins.kava.config.playlistId = playlist.id;
        }
        setPlayer(playerInstance);
    };

    const teardownPlayerListeners = () => {
        // remove listeners
        if (!player) {
            return;
        }
        player.removeEventListener(player.Event.Core.PLAYER_DESTROY, teardownPlayerListeners);
        player.removeEventListener(player.Event.Core.PLAYBACK_ENDED, handlePlaybackEnd);
    };

    const handlePlaybackEnd = () => {
        setShowLobby(true);
        endReactions();
    };

    // get time from server, then init accordingly
    useEffect(() => {
        const pollStream = () => {
            console.debug(">> polling primary stream... ");
            return LiveMediaHelper
                .pollStream(playerConfig.sources.hls[0].url)
                .then(() => {
                    console.debug(">> stream is alive");
                    // promise is always resolved with true
                    handleStreamStart();
                    return Promise.resolve(true);
                })
                .catch((err) => {
                    console.debug(">> stream is dead");
                    console.error(err);
                    // else (primary is not live)
                    if (playerConfig.plugins?.liveFallback?.fallbackUrl) {
                        // got secondary url, poll it
                        console.debug(">> polling secondary stream... ");
                        return LiveMediaHelper
                            .pollStream(playerConfig.plugins.liveFallback.fallbackUrl)
                            .then(() => {
                                console.debug(">> stream is alive");
                                // promise is always resolved with true
                                handleStreamStart();
                                return Promise.resolve(true);
                            })
                            .catch((err2) => {
                                // else (not live) show hero section
                                console.debug(">> stream is dead");
                                console.error(err2);
                                setShowLobby(true);
                                return Promise.resolve(false);
                            });
                    }
                    // no secondary, media is not live.
                    setShowLobby(true);
                    return Promise.resolve(false);
                });
        };

        const handleStreamStart = () => {
            // if live, show player
            setShowLobby(false);

            startReactions();
        };

        const decideWhatToDo = (serverNow: number) => {
            // logic of what to show when (player vs lobby), expected to only run once
            const now = serverNow ? serverNow : Date.now();
            const prestartInMs = scheduleEvent && scheduleEvent.preStartTime ? scheduleEvent.preStartTime * 1000 : 0;
            const postEndInMs = scheduleEvent && scheduleEvent.postEndTime ? scheduleEvent.postEndTime * 1000 : 0;

            ServerTimeHelper.setTimeForDiff(now);

            // if entry is recorded, show a normal player
            if (entry.recordedEntryId) {
                console.debug(">> found a recording");
                // for simulive only, don't play the part before the actual event started, if it isn't a separate entry
                if (
                    scheduleEvent
                    && scheduleEvent.sourceEntryId
                    && scheduleEvent.preStartTime
                    && !scheduleEvent.preStartEntryId
                ) {
                    const dummy = {
                        ...playerConfig,
                        sources: {},
                        session: {},
                        playback: {
                            ...playerConfig["playback"],
                            startTime: scheduleEvent.preStartTime,
                        },
                    };
                    setVodPlayerConfig(dummy);
                }
                else {
                    setVodPlayerConfig({
                        ...playerConfig,
                        sources: {},
                        session: {},
                    });
                }
                setShowLobby(false);
            }

            // if after scheduling window - event should be over but may still be playing
            else if (schedulingData.to + postEndInMs < now) {
                console.debug(">> time is over");
                // only poll if manual. Simulive should just be ended.
                if (!scheduleEvent || !scheduleEvent.sourceEntryId) {
                    // poll m3u8 (playerConfig.sources.hls.url)
                    pollStream();
                }
                else {
                    setShowLobby(true);
                }
            }

            // if more than 12 hours before scheduling window, show hero section and do nothing.
            else if (schedulingData.from - prestartInMs - now > TWELVE_HOURS) {
                console.debug(">> it is not yet time, more than 12 hours before scheduling window");
                setShowLobby(true);
            }

            // if more than 1 hour before scheduling window, show hero section and set timeout to refresh in an hour
            else if (schedulingData.from - prestartInMs - now > ONE_HOUR) {
                console.debug(">> it is not yet time");
                const n = Math.floor((schedulingData.from - prestartInMs - now) / ONE_HOUR);
                console.debug("will reload in " + n + " hours");
                safeSetTimeout(
                    () => {
                        window.location.reload();
                    },
                    n * ONE_HOUR
                );
                setShowLobby(true);
            }

            // if before scheduling window (and prestart), show hero section
            else if (schedulingData.from - prestartInMs > now) {
                console.debug(">> it is not yet time");
                // set timeout to start polling the stream when scheduling window opens (consider prestart + 30 secs)
                const timeToStartPolling = schedulingData.from - prestartInMs - now;
                console.debug("will poll in " + timeToStartPolling / 1000 + " secs");
                safeSetTimeout(
                    () => {
                        pollStream().then((isLive) => {
                            if (!isLive) {
                                // start polling every 10 secs
                                window.clearInterval(pollingInterval.current);
                                pollingInterval.current = window.setInterval(
                                    () => {
                                        pollStream().then((result) => {
                                            if (result) {
                                                window.clearInterval(pollingInterval.current);
                                            }
                                        });
                                    },
                                    STREAM_POLL_INTERVAL
                                );
                            }
                        });
                    },
                    timeToStartPolling
                );
                setShowLobby(true);
            }

            // if inside scheduling window
            else if (schedulingData.from - prestartInMs < now && schedulingData.to + postEndInMs > now) {
                console.debug(">> the time is now");
                // poll m3u8
                pollStream().then((result) => {
                    if (!result) {
                        // keep polling
                        window.clearInterval(pollingInterval.current);
                        pollingInterval.current = window.setInterval(
                            () => {
                                pollStream().then((isLive) => {
                                    if (isLive) {
                                        // no need to keep polling if stream is live
                                        window.clearInterval(pollingInterval.current);
                                    }
                                });
                            },
                            STREAM_POLL_INTERVAL
                        );
                    }
                });
            }
        };
        // get server time then decide what to do
        ServerTimeHelper
            .getServerTime(serviceUrl)
            .then((value) => {
                decideWhatToDo(value);
            })
            .catch((err) => {
                console.debug(">> failed getting system time, using local", err);
                decideWhatToDo(Date.now());
            });
        return () => {
            window.clearInterval(pollingInterval.current);
        };
    }, [entry.recordedEntryId, playerConfig, scheduleEvent, schedulingData.from, schedulingData.to]);

    const { statusLabel, actionsToPass } = getPropsToPass(entry, schedulingData, actions, isReadOnly);

    const sizedThumbnailUrl = useThumbnailSizing(thumbnailUrl);

    const media = useMemo(
        () => ({ entryId: entry.recordedEntryId, ks: playerConfig.ks as string }),
        [entry, playerConfig]
    );

    return (
        <CommonHeroLayout collapsedHero={props.collapse}>
            <>
                {showLobby && (
                    <Lobby
                        thumbnailUrl={sizedThumbnailUrl}
                        statusLabel={statusLabel}
                        title={entry.name}
                        schedulingData={schedulingData}
                        actions={actionsToPass}
                        data={<MediaLobbyDataContainer {...props} />}
                    />
                )}
                {!showLobby && (
                    <>
                        {!!entry.recordedEntryId && (
                            <Player media={media} config={vodPlayerConfig} className="kms-ds-media-page-player" />
                        )}
                        {!entry.recordedEntryId && (
                            <LiveMediaPlayerV3
                                config={playerConfig}
                                onError={() => void 0}
                                onReady={setupPlayerListeners}
                                className="kms-ds-media-page-player"
                            />
                        )}
                    </>
                )}
            </>
        </CommonHeroLayout>
    );
};

export default VirtualEventHero;
